import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useChannelApiKeys,
  useChannelReauth,
  useChannelScopes,
  useCreateChannelApiKey,
  useFeatures,
  useRevokeApiKey,
  useSaveFeatures,
  useSubscriptions,
} from '../../api/hooks';
import { buildColonTree } from '../../domain/tree';
import { Button, ErrorNote, Loading, OneTimeSecret, Section, ScopeList, TreeSelect } from '../../ui';

export function ScopesPage(): JSX.Element {
  const { botId = '', channelId = '' } = useParams();
  const scopes = useChannelScopes(botId, channelId);
  const reauth = useChannelReauth(botId, channelId);
  if (scopes.isPending) return <Loading />;
  if (scopes.isError) return <ErrorNote>{scopes.error.message}</ErrorNote>;
  const status = scopes.data!;
  const nodes = buildColonTree(status.required.length ? status.required : [...status.granted, ...status.missing]);
  const selected = new Set(status.granted);
  return (
    <Section
      title="Scopes & accès"
      actions={
        status.missing.length ? (
          <Button
            data-testid="create-reauth"
            variant="primary"
            loading={reauth.isPending}
            onClick={() => reauth.mutate({})}
          >
            Créer une réauth
          </Button>
        ) : null
      }
    >
      {reauth.data?.url ? <OneTimeSecret value={reauth.data.url} /> : null}
      <ScopeList required={status.required} granted={status.granted} missing={status.missing} />
      <div style={{ marginTop: 'var(--tbm-space-4)' }}>
        <TreeSelect nodes={nodes} selected={selected} readOnly />
      </div>
    </Section>
  );
}

export function FeaturesPage(): JSX.Element {
  const { botId = '', channelId = '' } = useParams();
  const features = useFeatures(botId, channelId);
  const save = useSaveFeatures(botId, channelId);
  const [draft, setDraft] = useState<Record<string, string>>({});
  if (features.isPending) return <Loading />;
  const rows = features.data ?? [];
  return (
    <Section
      title="Fonctionnalités"
      actions={
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={Object.keys(draft).length === 0}
          onClick={() =>
            save.mutate(
              rows.map((feature) => ({
                featureId: feature.id,
                desiredState: draft[feature.id] ?? feature.desiredState,
              })),
              { onSuccess: () => setDraft({}) },
            )
          }
        >
          Enregistrer
        </Button>
      }
    >
      {rows.map((feature) => {
        const state = draft[feature.id] ?? feature.desiredState;
        return (
          <label key={feature.id} className="tbm-check" style={{ display: 'flex', padding: 'var(--tbm-space-2) 0' }}>
            <input
              type="checkbox"
              checked={state === 'enabled'}
              onChange={(e) => setDraft((c) => ({ ...c, [feature.id]: e.target.checked ? 'enabled' : 'disabled' }))}
            />
            <span>
              <strong>{feature.label}</strong>
              <div className="muted">{feature.category} · {feature.grantMode}</div>
            </span>
          </label>
        );
      })}
    </Section>
  );
}

export function ChannelApiKeysPage(): JSX.Element {
  const { botId = '', channelId = '' } = useParams();
  const keys = useChannelApiKeys(botId, channelId);
  const create = useCreateChannelApiKey(botId, channelId);
  const revoke = useRevokeApiKey(botId, channelId);
  const subs = useSubscriptions(botId, channelId);
  const [label, setLabel] = useState('clé');
  const eventTypes = useMemo(
    () => (subs.data?.plan ?? []).filter((p) => p.desired).map((p) => p.type),
    [subs.data],
  );
  if (keys.isPending) return <Loading />;
  return (
    <Section
      title="Clés API"
      actions={
        <Button variant="primary" loading={create.isPending} onClick={() => create.mutate({ label, eventTypes })}>
          Créer
        </Button>
      }
    >
      <input className="tbm-input" value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Libellé" />
      {create.data?.secret ? <OneTimeSecret value={create.data.secret} /> : null}
      {(keys.data ?? []).map((key) => (
        <div key={key.keyId} className="tbm-toolbar">
          <code className="tbm-id">{key.keyId}</code>
          <span>{key.label}</span>
          <Button size="sm" variant="danger" onClick={() => revoke.mutate(key.keyId)}>Révoquer</Button>
        </div>
      ))}
    </Section>
  );
}
